import React, { useMemo, useState } from "react";
import {
  Table,
  Button,
  Dropdown,
  Menu,
  message,
  Modal,
  Form,
  Input,
  Space,
  Tag,
} from "antd";
import { CreditCardOutlined, MoreOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import {
  getAllAccounts,
  editUserAcc,
  deleteUserAcc,
} from "../actions/acc_actions";
import ContentLayout from "../components/layout/ContentLayout";
import Search from "antd/es/input/Search";
import { IoSearch } from "react-icons/io5";
import debounce from "debounce";
import SubscriptionDetailsModal from "../components/SubscriptionDetailsModal";
import { getAllSubscriptions } from "../actions/subscription_actions";

const billingStatusColors = {
  active: "green",
  inactive: "gold",
  past_due: "red",
  cancelling: "orange",
  cancelled: "default",
  overdue: "red",
};

const billingStatusLabels = {
  inactive: "PENDING ACTIVATION",
  past_due: "PAST DUE",
};

export default function UsersAccounts() {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [search, setSearch] = useState();

  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [billingAccount, setBillingAccount] = useState(null);
  const [form] = Form.useForm();

  const {
    data: accountsData,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["user-accounts", page, limit, search],
    queryFn: () => getAllAccounts({ page, limit, search }),
    keepPreviousData: true,
  });

  const accounts = useMemo(
    () => accountsData?.body?.accounts || [],
    [accountsData],
  );
  const pagination = accountsData?.pagination || {};
  const accountIds = useMemo(
    () => accounts.map((account) => account.urn).filter(Boolean).join(","),
    [accounts],
  );
  const {
    data: accountBillingData,
    isLoading: billingLoading,
    refetch: refetchBilling,
  } = useQuery({
    queryKey: ["user-account-billing", accountIds],
    queryFn: () => getAllSubscriptions({ page: 1, limit: 100, accountIds }),
    enabled: Boolean(accountIds),
    retry: false,
  });
  const billingByAccount = useMemo(() => {
    const map = new Map();
    (accountBillingData?.docs || []).forEach((subscription) => {
      if (subscription.account_urn && !map.has(subscription.account_urn)) {
        map.set(subscription.account_urn, subscription);
      }
    });
    return map;
  }, [accountBillingData]);

  // Open edit modal
  const openEditModal = (record) => {
    setEditingAccount(record);
    form.setFieldsValue({ name: record.name, slug: record.slug });
    setEditModalVisible(true);
  };

  // Save edit changes
  const handleEditSave = async () => {
    try {
      const values = await form.validateFields();
      await editUserAcc(editingAccount.urn, values);
      message.success("Account updated successfully");
      setEditModalVisible(false);
      refetch();
    } catch (error) {
      message.error("Failed to update account");
    }
  };

  // Handle delete with confirmation
  const handleDelete = (record) => {
    Modal.confirm({
      title: `Delete ${record.name}?`,
      content: "This action cannot be undone.",
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await deleteUserAcc(record.urn);
          message.success("Account deleted");
          refetch();
        } catch (error) {
          message.error("Failed to delete account");
        }
      },
    });
  };

  // Nested expanded table for subscriptions & addons
  const expandedRowRender = (record) => {
    const subs = record.extraData?.subscriptions || [];
    const subRows = subs.flatMap((sub) =>
      sub.addons.map((addon, idx) => ({
        key: `${sub.type}-${idx}`,
        subscriptionType: sub.type,
        addonKey: addon.key,
        isTrial: addon.is_trial ? "Yes" : "No",
        enabledAt: new Date(addon.enabled_at).toLocaleDateString(),
        price: addon.agreed_price?.["monthly-rolling"] ?? "-",
      }))
    );

    const nestedColumns = [
      {
        title: "Subscription Type",
        dataIndex: "subscriptionType",
        key: "subscriptionType",
      },
      { title: "Addon Key", dataIndex: "addonKey", key: "addonKey" },
      { title: "Trial", dataIndex: "isTrial", key: "isTrial" },
      { title: "Enabled At", dataIndex: "enabledAt", key: "enabledAt" },
      { title: "Agreed Price", dataIndex: "price", key: "price" },
    ];

    return (
      <Table
        columns={nestedColumns}
        dataSource={subRows}
        pagination={false}
        rowKey="key"
        size="small"
      />
    );
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      responsive: ["xs", "sm", "md", "lg"],
    },
    { title: "Slug", dataIndex: "slug", key: "slug", responsive: ["md", "lg"] },
    {
      title: "Client URN",
      dataIndex: "clientUrn",
      key: "clientUrn",
      responsive: ["lg"],
    },
    {
      title: "Billing",
      key: "billing",
      render: (record) => {
        const subscription = billingByAccount.get(record.urn);
        const status = subscription?.status;
        return (
          <Space wrap>
            <Tag color={billingStatusColors[status] || "default"}>
              {billingLoading
                ? "LOADING"
                : billingStatusLabels[status] || status?.toUpperCase() || "NO SUBSCRIPTION"}
            </Tag>
            <Button
              size="small"
              icon={<CreditCardOutlined />}
              onClick={() => setBillingAccount({ account: record, subscription })}
            >
              Manage billing
            </Button>
          </Space>
        );
      },
    },
    {
      title: "Settings",
      key: "settings",
      render: (record) => (
        <Dropdown
          overlay={
            <Menu>
              <Menu.Item key="edit" onClick={() => openEditModal(record)}>
                Edit
              </Menu.Item>
              <Menu.Item
                key="delete"
                danger
                onClick={() => handleDelete(record)}
              >
                Delete
              </Menu.Item>
            </Menu>
          }
          trigger={["click"]}
        >
          <Button icon={<MoreOutlined />} />
        </Dropdown>
      ),
    },
  ];

  const onSearch = (value) => {
    setSearch(value);
    setPage(1);
  };

  // Debounced function to update search state
  const debouncedSearch = useMemo(
    () =>
      debounce((value) => {
        setSearch(value);
        setPage(1);
      }, 500), // 500ms debounce delay
    []
  );

  // onChange handler for Input
  const handleChange = (e) => {
    debouncedSearch(e.target.value);
  };

  return (
    <ContentLayout
      header={
        <>
          <Search
            placeholder="search"
            enterButton={<IoSearch />}
            size="large"
            onSearch={onSearch}
            className="search_bar"
            onChange={handleChange}
            defaultValue={search}
            allowClear
          />
          <Table
            loading={isLoading}
            columns={columns}
            dataSource={accounts}
            rowKey="urn"
            expandable={{ expandedRowRender }}
            pagination={{
              current: pagination.currentPage || page,
              total: pagination.totalCount || 0,
              pageSize: pagination.pageSize || limit,
              showSizeChanger: true,
              onChange: (newPage, newLimit) => {
                setPage(newPage);
                setLimit(newLimit);
              },
              onShowSizeChange: (current, newSize) => {
                setPage(1);
                setLimit(newSize);
              },
            }}
            scroll={{ x: true }}
          />
          {/* Edit Modal */}
          <Modal
            open={editModalVisible}
            title="Edit Account"
            onCancel={() => setEditModalVisible(false)}
            onOk={handleEditSave}
            okText="Save"
          >
            <Form form={form} layout="vertical">
              <Form.Item
                name="name"
                label="Name"
                rules={[{ required: true, message: "Please enter a name" }]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                name="slug"
                label="Slug"
                rules={[{ required: true, message: "Please enter a slug" }]}
              >
                <Input />
              </Form.Item>
            </Form>
          </Modal>
          <SubscriptionDetailsModal
            open={!!billingAccount}
            onClose={() => setBillingAccount(null)}
            subscription={billingAccount?.subscription}
            accountUrn={billingAccount?.account?.urn}
            accountName={billingAccount?.account?.name}
            onUpdated={() => {
              refetch();
              refetchBilling();
            }}
          />
        </>
      }
    />
  );
}
